import { ChangeEvent, useCallback, useId, useMemo } from 'react';
import { LoadingSkeleton } from '@coinbase/cdp-react/components/ui/LoadingSkeleton';
import { useConfig } from 'apps/cdp-web-demo/app/(main)/ConfigContext';
import { isDefaultAppearanceConfig } from 'apps/cdp-web-demo/app/(main)/utils/config';
import {
  AccentColor,
  AccountCreationType,
  AuthMethod,
  BorderRadius,
  FontFamily,
  HexColor,
  ThemeMode,
} from 'apps/cdp-web-demo/app/types';
import { Switch } from '@cbhq/cds-web/controls/Switch';
import { TextInput } from '@cbhq/cds-web/controls/TextInput';
import { SegmentedTabs, SegmentedTabsProps } from '@cbhq/cds-web/tabs/SegmentedTabs';
import { Text } from '@cbhq/cds-web/typography';

import { AccountCreationTypeField } from '../AccountCreationType/AccountCreationTypeField';
import { AuthMethodsField } from '../AuthMethods/AuthMethodsField';
import { BorderRadiusField } from '../BorderRadius/BorderRadiusField';
import { Collapsible } from '../Collapsible/Collapsible';
import { ColorField } from '../Color/ColorField';
import { FontFamilyField } from '../FontFamily/FontFamilyField';
import { LoadingInput } from '../LoadingInput/LoadingInput';
import { LoadingRadioInputs } from '../LoadingInput/LoadingRadioInputs';
import { UserActions } from '../UserActions/UserActions';

import styles from './ConfigForm.module.css';

type ConfigFormProps = {
  isInitialized: boolean;
  isLoggedIn: boolean;
  addMfa: () => void;
  exportWallet: () => void;
  linkProfile: () => void;
  sendTransaction: () => void;
};

function ConfigForm({
  isInitialized,
  isLoggedIn,
  exportWallet,
  addMfa,
  linkProfile,
  sendTransaction,
}: ConfigFormProps) {
  const resetButtonDesriptionId = useId();
  const { state, dispatch, isHydrated } = useConfig();
  const { accentColor, borderRadius, config, customColor, fontFamily, themeMode } = state;

  const isDefaultAppearance = useMemo(
    () => (!isHydrated ? true : isDefaultAppearanceConfig(state)),
    [state, isHydrated],
  );
  const themeOptions = useMemo<{ label: string; id: ThemeMode }[]>(
    () => [
      { id: 'light', label: 'Light' },
      { id: 'dark', label: 'Dark' },
    ],
    [],
  );
  const { ethereum, solana } = config;
  const { createOnLogin: ethereumCreateOnLogin } = ethereum ?? {};
  const { createOnLogin: solanaCreateOnLogin } = solana ?? {};
  const accountType = useMemo<AccountCreationType>(() => {
    if (ethereumCreateOnLogin) {
      return ethereumCreateOnLogin === 'smart' ? 'evm-smart' : 'evm-eoa';
    }
    if (solanaCreateOnLogin) {
      return 'solana';
    }
    return 'evm-smart';
  }, [ethereumCreateOnLogin, solanaCreateOnLogin]);

  const activeTheme = useMemo<SegmentedTabsProps['activeTab']>(
    () => themeOptions.find((option) => option.id === themeMode) ?? null,
    [themeMode, themeOptions],
  );

  const textInputStyle = useMemo(() => ({ paddingTop: '0.5em', paddingBottom: '0.5em' }), []);

  const selectedAuthMethods = useMemo(() => {
    return config.authMethods ?? [];
  }, [config.authMethods]);

  const handleAppLogoChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'SET_APP_LOGO', payload: { appLogoUrl: e.target.value } });
    },
    [dispatch],
  );
  const handleAppNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'SET_APP_NAME', payload: { appName: e.target.value } });
    },
    [dispatch],
  );

  const handleSetAuthMethods = useCallback(
    (values: AuthMethod[]) => {
      dispatch({ type: 'SET_AUTH_METHODS_ORDER', payload: { authMethods: values } });
    },
    [dispatch],
  );

  const handleToggleAuthMethod = useCallback(
    (method: AuthMethod, checked: boolean) => {
      dispatch({ type: 'TOGGLE_AUTH_METHOD', payload: { method, checked } });
    },
    [dispatch],
  );

  const handleSetAccountCreationType = useCallback(
    (newAccountCreationType: AccountCreationType) => {
      dispatch({
        type: 'SET_ACCOUNT_CREATION_TYPE',
        payload: { accountCreationType: newAccountCreationType },
      });
    },
    [dispatch],
  );

  const handleToggleCoinbaseFooter = useCallback(() => {
    dispatch({ type: 'TOGGLE_SHOW_COINBASE_FOOTER' });
  }, [dispatch]);

  const handleSetAccentColor = useCallback(
    (color: AccentColor, custom?: HexColor) => {
      dispatch({ type: 'SET_ACCENT_COLOR', payload: { accentColor: color, customColor: custom } });
    },
    [dispatch],
  );

  const handleSetFontFamily = useCallback(
    (newFontFamily: FontFamily) => {
      dispatch({ type: 'SET_FONT_FAMILY', payload: { fontFamily: newFontFamily } });
    },
    [dispatch],
  );

  const handleSetBorderRadius = useCallback(
    (newBorderRadius: BorderRadius) => {
      dispatch({ type: 'SET_BORDER_RADIUS', payload: { borderRadius: newBorderRadius } });
    },
    [dispatch],
  );

  const handleThemeChange = useCallback(
    (tab: SegmentedTabsProps['activeTab'] | null) => {
      if (!(tab?.id === 'light' || tab?.id === 'dark')) {
        return;
      }

      document.documentElement.setAttribute('data-theme', tab.id);
      dispatch({ type: 'SET_THEME_MODE', payload: { themeMode: tab.id } });
    },
    [dispatch],
  );

  const handleResetAppearance = useCallback(() => {
    if (isDefaultAppearance) {
      return;
    }
    dispatch({ type: 'RESET_APPEARANCE_CONFIG' });
    document.documentElement.setAttribute('data-theme', 'light');
  }, [dispatch, isDefaultAppearance]);

  return (
    <div>
      {isInitialized && isLoggedIn && (
        <UserActions
          accountCreationType={accountType}
          addMfa={addMfa}
          exportWallet={exportWallet}
          linkProfile={linkProfile}
          sendTransaction={sendTransaction}
        />
      )}
      <form className={styles.form}>
        {!isInitialized && (
          <div className={styles['loading-wrapper']}>
            <LoadingSkeleton className={styles['loading-heading']} />
            <LoadingSkeleton className={styles['loading-row']} />
            <LoadingSkeleton className={styles['loading-row']} />
            <LoadingSkeleton className={styles['loading-row']} />
            <LoadingSkeleton className={styles['loading-row']} />
            <LoadingSkeleton className={styles['loading-row']} />
          </div>
        )}
        {isInitialized && !isLoggedIn && (
          <AuthMethodsField
            authMethods={selectedAuthMethods}
            setAuthMethods={handleSetAuthMethods}
            toggleAuthMethod={handleToggleAuthMethod}
          />
        )}

        <Collapsible triggerLabel="Appearance">
          <div className={styles['appearance-content']}>
            <fieldset>
              <legend>
                <Text as="span" font="label1" className={styles['theme-mode-legend']}>
                  Theme
                </Text>
              </legend>
              <SegmentedTabs
                tabs={themeOptions}
                activeTab={activeTheme}
                onChange={handleThemeChange}
              />
            </fieldset>

            <ColorField
              accentColor={accentColor}
              customColor={customColor}
              themeMode={themeMode}
              setAccentColor={handleSetAccentColor}
            />

            {!isHydrated && (
              <>
                <LoadingInput label="Logo" />
                <LoadingInput label="App name" />
                <LoadingInput label="Font" />
                <LoadingRadioInputs label="Border radius" />
              </>
            )}
            {isHydrated && (
              <>
                <TextInput
                  label="Logo"
                  placeholder="Enter image URL"
                  value={config.appLogoUrl}
                  onChange={handleAppLogoChange}
                  accessibilityLabel="Logo"
                  helperTextErrorIconAccessibilityLabel="Error"
                  style={textInputStyle}
                />

                <TextInput
                  label="App name"
                  placeholder="Enter app name"
                  value={config.appName}
                  onChange={handleAppNameChange}
                  accessibilityLabel="App name"
                  helperTextErrorIconAccessibilityLabel="Error"
                  style={textInputStyle}
                />

                <FontFamilyField fontFamily={fontFamily} setFontFamily={handleSetFontFamily} />

                <BorderRadiusField
                  setBorderRadius={handleSetBorderRadius}
                  borderRadius={borderRadius}
                />
              </>
            )}

            {!isLoggedIn && isInitialized && (
              <div className={styles['switch-wrapper']}>
                <Switch
                  onChange={handleToggleCoinbaseFooter}
                  checked={config.showCoinbaseFooter ?? false}
                >
                  <Text font="label1">Show Coinbase branding</Text>
                </Switch>
              </div>
            )}

            {!isLoggedIn && isInitialized && (
              <AccountCreationTypeField
                accountType={accountType}
                setAccountCreationType={handleSetAccountCreationType}
              />
            )}

            <div>
              <button
                aria-label="reset appearance settings"
                aria-disabled={isDefaultAppearance}
                aria-describedby={isDefaultAppearance ? resetButtonDesriptionId : undefined}
                type="button"
                onClick={handleResetAppearance}
                className={styles['reset-appearance-button']}
              >
                <Text color="fgPrimary" font="label1">
                  Reset to default
                </Text>
              </button>
              {isDefaultAppearance && (
                <span id={resetButtonDesriptionId} className="sr-only">
                  Appearance settings are already set to their defaults
                </span>
              )}
            </div>
          </div>
        </Collapsible>
      </form>
    </div>
  );
}

export { ConfigForm };
